import type {
  CustomerManagerBillingAnomaliesResponse,
  CustomerManagerPaymentOrdersResponse,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { opsApiRequest } from '../api/client.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Panel, TBody, THead, Table, Td, Th, Tr } from '../ui/index.ts';
import { formatCnyCents, formatLocalTimestamp, formatUsdMicrosOrCents } from './formatters.ts';
import { StatusChip } from './StatusChip.tsx';

/**
 * Read-only "money status" section for Customer 360 (epic #1035 E6-D3,
 * #1067): the owned customer's payment orders (answers "充了钱怎么没到账")
 * and open billing anomalies.
 *
 * Self-contained by design — owns its two fetch hooks and renders from a
 * single mount line in `CustomerManagerCustomer360Page.tsx`, so parallel
 * 360 sub-issues stay mergeable. Server-side, ops-api resolves the
 * customer through the scoped 360 lookup and pins `account_id` into the
 * platform query; the payload is the sales projection (tenant-money
 * facts only).
 *
 * Deliberately renders ZERO mutation controls: reconcile and
 * anomaly-resolve stay on the admin-gated platform surfaces, for admin
 * sessions too — this section is a fact sheet, not a console.
 */

const MONEY_STATUS_ROOT_KEY = ['customer-manager-money-status'] as const;

function useCustomerPaymentOrders(
  orgId: string,
): UseQueryResult<CustomerManagerPaymentOrdersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MONEY_STATUS_ROOT_KEY, 'payment-orders', orgId] as const,
    queryFn: ({ signal }) =>
      opsApiRequest<CustomerManagerPaymentOrdersResponse>(
        `/v1/ops/v2/customer-manager/customers/${encodeURIComponent(orgId)}/payment-orders`,
        { signal },
        fetchImpl,
      ),
    enabled: orgId.length > 0,
  });
}

function useCustomerBillingAnomalies(
  orgId: string,
): UseQueryResult<CustomerManagerBillingAnomaliesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MONEY_STATUS_ROOT_KEY, 'billing-anomalies', orgId] as const,
    // No `resolvedFilter` param — the platform default is `unresolved`,
    // which is exactly the "open anomalies" lens this section wants.
    queryFn: ({ signal }) =>
      opsApiRequest<CustomerManagerBillingAnomaliesResponse>(
        `/v1/ops/v2/customer-manager/customers/${encodeURIComponent(orgId)}/billing-anomalies`,
        { signal },
        fetchImpl,
      ),
    enabled: orgId.length > 0,
  });
}

const ORDER_STATUS_LABEL_KEYS: Record<
  CustomerManagerPaymentOrdersResponse['orders'][number]['status'],
  I18nKey
> = {
  pending: 'customer_manager_360.money.status.pending',
  paid: 'customer_manager_360.money.status.paid',
  expired: 'customer_manager_360.money.status.expired',
  failed: 'customer_manager_360.money.status.failed',
  refunded: 'customer_manager_360.money.status.refunded',
};

const ORDER_STATUS_VARIANTS: Record<
  CustomerManagerPaymentOrdersResponse['orders'][number]['status'],
  'ok' | 'warning' | 'error' | 'neutral'
> = {
  pending: 'warning',
  paid: 'ok',
  expired: 'neutral',
  failed: 'error',
  refunded: 'neutral',
};

const PAY_TYPE_LABEL_KEYS: Record<
  CustomerManagerPaymentOrdersResponse['orders'][number]['pay_type'],
  I18nKey
> = {
  alipay: 'customer_manager_360.money.pay_type.alipay',
  wxpay: 'customer_manager_360.money.pay_type.wxpay',
};

function SectionState({ labelKey }: { readonly labelKey: I18nKey }): JSX.Element {
  const t = useT();
  return <p className="m-0 px-4 py-3 text-body text-paper-low">{t(labelKey)}</p>;
}

function PaymentOrdersPanel({ orgId }: { readonly orgId: string }): JSX.Element {
  const t = useT();
  const orders = useCustomerPaymentOrders(orgId);
  const rows = orders.data?.orders ?? [];
  return (
    <Panel
      testId="customer-manager-360-money-orders"
      caption={t('customer_manager_360.money.orders')}
      count={orders.data === undefined ? undefined : rows.length}
    >
      <p className="m-0 border-b border-line-soft px-4 py-2.5 text-dense text-paper-low">
        {t('customer_manager_360.money.orders_hint')}
      </p>
      {orders.isPending ? <SectionState labelKey="customer_manager_360.money.loading" /> : null}
      {orders.isError ? <SectionState labelKey="customer_manager_360.money.error" /> : null}
      {orders.isSuccess && rows.length === 0 ? (
        <SectionState labelKey="customer_manager_360.money.empty.orders" />
      ) : null}
      {orders.isSuccess && rows.length > 0 ? (
        <Table className="rounded-none border-0">
          <THead>
            <Tr>
              <Th>{t('customer_manager_360.money.col.created')}</Th>
              <Th>{t('customer_manager_360.money.col.order_no')}</Th>
              <Th>{t('customer_manager_360.money.col.pay_type')}</Th>
              <Th num>{t('customer_manager_360.money.col.amount')}</Th>
              <Th>{t('customer_manager_360.money.col.status')}</Th>
              <Th>{t('customer_manager_360.money.col.paid_at')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((order) => (
              <Tr key={order.order_id}>
                <Td className="whitespace-nowrap font-mono text-paper-low">
                  {formatLocalTimestamp(order.created_at)}
                </Td>
                <Td className="font-mono">{order.out_trade_no}</Td>
                <Td>{t(PAY_TYPE_LABEL_KEYS[order.pay_type])}</Td>
                <Td num className="font-mono">
                  {formatCnyCents(order.amount_cny_cents, false)}
                </Td>
                <Td>
                  <StatusChip
                    variant={ORDER_STATUS_VARIANTS[order.status]}
                    label={t(ORDER_STATUS_LABEL_KEYS[order.status])}
                  />
                </Td>
                <Td className="whitespace-nowrap font-mono text-paper-low">
                  {formatLocalTimestamp(order.paid_at)}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      ) : null}
    </Panel>
  );
}

function BillingAnomaliesPanel({ orgId }: { readonly orgId: string }): JSX.Element {
  const t = useT();
  const anomalies = useCustomerBillingAnomalies(orgId);
  const rows = anomalies.data?.anomalies ?? [];
  return (
    <Panel
      testId="customer-manager-360-money-anomalies"
      caption={t('customer_manager_360.money.anomalies')}
      count={anomalies.data === undefined ? undefined : rows.length}
    >
      <p className="m-0 border-b border-line-soft px-4 py-2.5 text-dense text-paper-low">
        {t('customer_manager_360.money.anomalies_hint')}
      </p>
      {anomalies.isPending ? <SectionState labelKey="customer_manager_360.money.loading" /> : null}
      {anomalies.isError ? <SectionState labelKey="customer_manager_360.money.error" /> : null}
      {anomalies.isSuccess && rows.length === 0 ? (
        <SectionState labelKey="customer_manager_360.money.empty.anomalies" />
      ) : null}
      {anomalies.isSuccess && rows.length > 0 ? (
        <div className="flex flex-col gap-2 p-4">
          {rows.map((anomaly) => (
            <div
              key={anomaly.id}
              className="grid gap-3 rounded-lg bg-well px-3 py-3 text-dense md:grid-cols-[1fr_auto]"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate font-medium text-paper">{anomaly.model}</span>
                <span className="text-paper-low">{anomaly.reason}</span>
                <span className="font-mono text-paper-low">
                  {formatLocalTimestamp(anomaly.created_at)}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-mono text-body font-semibold text-paper">
                  {formatUsdMicrosOrCents(anomaly.amount_usd_micros, anomaly.amount_usd_cents, false)}
                </span>
                <StatusChip
                  variant={anomaly.resolved_at === null ? 'warning' : 'ok'}
                  label={
                    anomaly.resolved_at === null
                      ? t('customer_manager_360.money.anomaly.open')
                      : t('customer_manager_360.money.anomaly.resolved')
                  }
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

export function CustomerMoneyStatusSection({ orgId }: { readonly orgId: string }): JSX.Element {
  return (
    <div
      data-testid="customer-manager-360-money-status"
      className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.75fr)]"
    >
      <PaymentOrdersPanel orgId={orgId} />
      <BillingAnomaliesPanel orgId={orgId} />
    </div>
  );
}
